import { api } from '../client';

export type UploadPurpose =
  | 'USER_AVATAR'
  | 'USER_KYC_DOCUMENT'
  | 'SHIPMENT_DOCUMENT'
  | 'PAYMENT_PROOF'
  | 'CHARGE_ATTACHMENT'
  | 'INVOICE_PDF';

export interface CreateUploadIntentRequest {
  purpose: UploadPurpose;
  targetId?: number;
  documentTag?: string;
  fileName: string;
  contentType: string;
  declaredSizeBytes: number;
  checksumSha256?: string;
}

export interface UploadIntentResponse {
  intentId: string;
  purpose: UploadPurpose;
  status: 'INITIATED' | 'COMPLETED' | 'EXPIRED' | 'ABORTED';
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
  objectKey: string;
  expiresAt: string;
  createdAt: string;
}

export interface CompleteUploadResponse {
  intentId: string;
  status: 'COMPLETED';
  attachmentId: number | null;
  fileUrl: string;
  downloadUrl: string;
  completedAt: string;
}

export interface DirectUploadParams {
  file: File;
  purpose: UploadPurpose;
  targetId?: number;
  documentTag?: string;
  checksumSha256?: string;
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB limit

/**
 * Creates an upload intent record and retrieves an S3 presigned PUT URL with signed headers.
 */
export async function createUploadIntent(
  req: CreateUploadIntentRequest,
  signal?: AbortSignal,
): Promise<UploadIntentResponse> {
  const { data } = await api.post<UploadIntentResponse>(
    '/uploads/intent',
    req,
    ...(signal ? [{ signal }] : []),
  );
  return data;
}

/**
 * Signals completion of direct upload. The API verifies physical existence and bounds via S3 HEAD.
 */
export async function completeUploadIntent(
  intentId: string,
  signal?: AbortSignal,
): Promise<CompleteUploadResponse> {
  const { data } = await api.post<CompleteUploadResponse>(
    `/uploads/intent/${intentId}/complete`,
    ...(signal ? [undefined, { signal }] : []),
  );
  return data;
}

/**
 * Retrieves the state of an existing upload intent.
 */
export async function getUploadIntent(intentId: string): Promise<UploadIntentResponse> {
  const { data } = await api.get<UploadIntentResponse>(`/uploads/intent/${intentId}`);
  return data;
}

/**
 * End-to-end direct S3 upload coordinator:
 * 1. Validates local size limit (&le; 10 MB).
 * 2. Requests upload intent from rhino-api.
 * 3. Streams file directly to S3 via native HTTPS PUT (no API buffer / memory footprint).
 * 4. Completes intent idempotently, attaching the artifact in PostgreSQL.
 */
export async function uploadFileDirectToS3(
  params: DirectUploadParams,
): Promise<CompleteUploadResponse> {
  const { file, purpose, targetId, documentTag, checksumSha256 } = params;

  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error('File must be 10 MB or smaller');
  }

  const contentType = file.type && file.type.length > 0 ? file.type : 'application/octet-stream';

  const intent = await createUploadIntent({
    purpose,
    targetId,
    documentTag,
    fileName: file.name,
    contentType,
    declaredSizeBytes: file.size,
    checksumSha256,
  });

  const s3Response = await fetch(intent.uploadUrl, {
    method: 'PUT',
    headers: intent.requiredHeaders,
    body: file,
  });

  if (!s3Response.ok) {
    throw new Error(
      `Direct S3 upload failed: ${s3Response.statusText || 'Unknown Error'} (HTTP ${s3Response.status})`,
    );
  }

  return await completeUploadIntent(intent.intentId);
}
